import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { SecretsService } from '@generated/services/secrets.service';
import { SecretListItemResult } from '@generated/models/secret-list-item-result';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { environment } from '@env/environment';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
    TooltipModule,
    DeleteConfirmationComponent
],
  templateUrl: './settings.html',
  styleUrl: './settings.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsComponent implements OnInit {
  private secretsService = inject(SecretsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  secrets: SecretListItemResult[] = [];
  isLoading = false;
  readonly appName = environment.appName;

  // Delete confirmation dialog
  deleteDialogVisible = false;
  deletingSecret: SecretListItemResult | null = null;
  isDeleting = false;

  ngOnInit(): void {
    this.loadSecrets();
  }

  private loadSecrets(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.secretsService.apiSecretsGet().subscribe({
      next: (secrets) => {
        this.secrets = secrets || [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load secrets:', error);
        this.notificationService.showError('Failed to load secrets');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  addSecret(): void {
    this.router.navigate(['/secrets/new']);
  }

  editSecret(secret: SecretListItemResult): void {
    if (secret.id) {
      this.router.navigate(['/secrets', secret.id]);
    }
  }

  deleteSecret(secret: SecretListItemResult): void {
    this.deletingSecret = secret;
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.deletingSecret?.id) return;

    this.isDeleting = true;
    this.cdr.markForCheck();

    this.secretsService.apiSecretsIdDelete({ id: this.deletingSecret.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Secret deleted successfully');
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.deletingSecret = null;
        this.loadSecrets();
      },
      error: (error) => {
        console.error('Failed to delete secret:', error);
        this.notificationService.showError('Failed to delete secret');
        this.isDeleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.deletingSecret = null;
    this.cdr.markForCheck();
  }
}
